/*
 * Rebuy Smart Cart wiring for Horizon. See snippets/ra-cart-rebuy.liquid.
 *
 * - Loads Rebuy's script (about 400 KB) on the first interaction (tap, key or scroll). Only the cart
 *   page also loads it 5 s after load, so the cart is ready for checkout without a tap; elsewhere a
 *   visit with no interaction never downloads it (page-load speed).
 * - Pointing at, focusing or touching a cart link starts loading Rebuy, so it is often ready by the tap.
 * - A tap on a cart link opens the Smart Cart. If Rebuy is not ready within 4 s, or fails,
 *   the link goes to the cart page as normal.
 * - Cold open (Rebuy not drawn yet, e.g. the cart icon is the visit's first tap): a sized skeleton
 *   panel shows at once, and Rebuy's drawer is kept invisible (html.ra-rb-filling) while it slides
 *   in and fills, then swapped in when it has gone quiet. Its start-up redraws used to shift inside
 *   the open drawer (CLS 0.05-0.16 on phones, 0.01-0.03 at 1440).
 * - After Horizon adds something to the cart (its `shopify:cart:lines-update` event, once the
 *   request has finished) the Smart Cart opens, unless the add failed (the error stays visible on
 *   the page) or asked to be silent (`raSilent`, set by assets/ra-pdp-cart.js for `ra:cart-add`
 *   with `silent: true`). Rebuy opens itself on every cart change, so a silent add also sets
 *   Rebuy's own `skip_open` until that change has been handled.
 * - Once the page is idle after a tap or key started Rebuy, tells Rebuy the shopper has already
 *   interacted (see settleWhenIdle), so its held cart write and carousel redraw run then, not on
 *   the visitor's second tap.
 * - Rewords the drawer's trust line to the approved claim ("Trusted by 100,000+ anglers", Curtis
 *   2026-10-17, site-wide) in place of Rebuy's footer template's "2,000+ Verified Reviews". The real
 *   fix is in Rebuy's Smart Cart settings; this keeps the drawer right until then.
 * - Wallet buttons (Shop Pay, Apple Pay, Google Pay): the first time the drawer shows with items,
 *   the inert <template id="ra-wallet-buttons"> (snippets/ra-cart-rebuy.liquid) is cloned in. With
 *   Rebuy's Accelerated Checkout on, Rebuy moves the buttons into its slot (handed over here, since
 *   Rebuy stops looking after 25 s); with it off, they go into the slot directly. Never a second set:
 *   nothing is mounted when the slot or the page already has wallet buttons, and the direct mount is
 *   removed as soon as Rebuy fills the slot itself.
 * - The reel "Buy 3, get 1 free" row: Rebuy's ready callback appends it about 120 ms after a line
 *   draws, which moved everything under it. A line that will get the row reserves its 44px at once
 *   (data-ra-b3g1-wait, same rule as the callback's planFor: reels under 4 units with no free line,
 *   the Rod Sleeve under 8 units); the reservation gives way to the row.
 *   The NEW DEALS Smart Cart (2026-10-16, .debug/smartcart/new-cart.js) says per line instead, through
 *   window.__raTierPlan(li) and window.__raStarsPlan(li): one deal row on the first line of a product
 *   (its CSS sets the row height in --ra-tier-row-h / --ra-sleeve-row-h), stars only where the USA Yotpo count
 *   is above 0. Without those hooks (1/09 LIVE) the old rules apply unchanged.
 * - Savings follow the product pages: the deal saving only, never compare-at (syncSavings).
 * - Rebuy's cart is checked against /cart.js on every open and after every add (syncCart): an add that was the
 *   visit's first tap could leave the drawer on the cart from before the add until a reload.
 * - Rev 5.3: Rebuy also loads once the page is idle after its load event; it waits up to 30 s instead of giving
 *   up for the visit after 4 s (cart links fall back to the cart page after 12 s, or at once if Rebuy fails); no
 *   wallet set is mounted when the active Smart Cart hides wallets (hidesWallets).
 * - G10 (2026-10-19): a page view makes no cart and sends no cart write. Rebuy's attribute writes wait until the
 *   visitor has a cart (gateHeldWrites); its duplicate _barId / _rebuyCartId writes are sent once.
 * Every drawer sync is idempotent and writes only an attribute or moves one node, and the observer that
 * runs it watches children and text, not attributes, so no sync can trigger itself or Rebuy's code.
 */
(function () {
  const config = window.RA_REBUY;
  if (!config || !config.scriptUrl) return;

  const onCartPage = () => window.location.pathname === config.cartUrl;
  const smartCart = () => {
    const cart = window.Rebuy && window.Rebuy.SmartCart;
    return cart && typeof cart.show === 'function' ? cart : null;
  };

  let loading = null;

  const TRUST_FROM = /2,000\+\s*Verified\s+Reviews/i;
  const TRUST_TO = 'Trusted by 100,000+ anglers';

  function fixTrust(root) {
    for (const box of root.querySelectorAll('.ra-cro-trust')) {
      const walker = document.createTreeWalker(box, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (TRUST_FROM.test(node.nodeValue)) node.nodeValue = node.nodeValue.replace(TRUST_FROM, TRUST_TO);
      }
    }
  }

  /* ---- wallet buttons in the drawer ---- */
  const WALLETS = 'shop-pay-wallet-button, shopify-apple-pay-button, shopify-google-pay-button, shopify-paypal-button, shopify-amazon-pay-button';
  const SLOT = '[data-rebuy-cart-additional-checkout-buttons]';
  /** @type {HTMLElement | null} */
  let walletHost = null; // the clone, in <body> (handed to Rebuy) or in the slot (direct)
  let walletMode = ''; // '', 'rebuy' or 'direct'

  const acceleratedOn = () => {
    try {
      const parts = /** @type {any} */ (window.Rebuy).SmartCart.settings.components;
      const checkout = parts.find((/** @type {any} */ c) => c && c.type === 'checkout');
      return Boolean(checkout && checkout.settings && checkout.settings.accelerated_checkout && checkout.settings.accelerated_checkout.enabled);
    } catch {
      return false;
    }
  };

  /** Rebuy looks for the buttons at start-up and then for 25 s only: ask again once they exist. */
  function handOver() {
    const cart = /** @type {any} */ (smartCart());
    if (!cart || cart.isAcceleratedCheckoutPopulated || typeof cart.manageAcceleratedCheckout !== 'function') return;
    try {
      cart.manageAcceleratedCheckout();
    } catch {}
  }

  /** Runs `then` once `host` holds real wallet buttons (Shopify first renders an empty grid). */
  function whenButtons(host, then) {
    if (host.querySelector(WALLETS)) return then();
    const watch = new MutationObserver(() => {
      if (!host.querySelector(WALLETS)) return;
      watch.disconnect();
      then();
    });
    watch.observe(host, { childList: true, subtree: true });
    window.setTimeout(() => watch.disconnect(), 20000);
  }

  function hidesWallets() {
    if (/** @type {any} */ (window).__raHideWallets) return true;
    const settings = /** @type {any} */ (window).Rebuy?.SmartCart?.settings;
    return !acceleratedOn() && typeof settings?.custom_css === 'string' && /NO WALLETS variant/.test(settings.custom_css);
  }

  function syncWallets(/** @type {HTMLElement} */ drawer) {
    const slot = drawer.querySelector(SLOT);
    if (!slot) return;
    const ours = walletMode === 'direct' && walletHost && slot.contains(walletHost);
    // Rebuy has filled the slot itself: our direct set steps aside.
    if (walletMode === 'direct' && walletHost && (slot.querySelector('#rebuy-additional-checkout-buttons') || (!ours && slot.querySelector(WALLETS)))) {
      walletHost.remove();
      walletHost = null;
      walletMode = 'done';
      return;
    }
    // Vue redrew the slot: the direct set moves into the new one.
    if (walletMode === 'direct' && walletHost && !ours) {
      slot.append(walletHost);
      return;
    }
    if (walletMode) return;
    // The active Smart Cart hides wallets: mount none, so Shopify's wallet scripts (PayPal's SDK, ~370 KB) are never
    // fetched. Known from the cart itself: its Ready callback's flag (rev 5.3), or Accelerated Checkout off together
    // with the "NO WALLETS variant" layer in its custom CSS ("NEW DEALS – NO WALLETS", whatever callback it holds).
    // Carts that show wallets (1/09 LIVE: Accelerated Checkout on, no such layer) mount them as before.
    if (hidesWallets()) {
      walletMode = 'none';
      return;
    }
    if (!drawer.classList.contains('is-visible') || !drawer.classList.contains('has-items')) return;
    const tpl = /** @type {HTMLTemplateElement | null} */ (document.getElementById('ra-wallet-buttons'));
    // Already there (Rebuy's own set, or the cart page's container): never a second set.
    if (!tpl || slot.querySelector(WALLETS) || document.querySelector('.additional-checkout-buttons')) {
      walletMode = 'done';
      if (acceleratedOn()) handOver();
      return;
    }
    const host = /** @type {HTMLElement | null} */ (document.importNode(tpl.content, true).firstElementChild);
    if (!host) return;
    walletHost = host;
    // Straight into the slot, in the same frame the drawer shows, so Shopify's button-sized
    // placeholder holds the room from the start and nothing in the footer moves later.
    host.removeAttribute('style');
    if (acceleratedOn()) {
      // Rebuy appends this container to the slot it is already in (no move), then keeps it.
      walletMode = 'rebuy';
      slot.append(host);
      handOver();
      whenButtons(host, handOver);
    } else {
      walletMode = 'direct';
      // Not .additional-checkout-buttons: Rebuy clears that class from the drawer while its setting is off.
      host.className = 'ra-wallets';
      slot.append(host);
    }
    try {
      /** @type {any} */ (window).Shopify?.PaymentButton?.init?.();
    } catch {}
  }

  /* ---- smaller "Complete your setup" images (2026-10-19) ----
     Rebuy's widget template asks for every recommendation image at 400x400 (itemImage(..., '400x400')), shown at
     80 px in the drawer. Its sizeImage() puts "_400x400" before the last ".ext" of the image URL, and the image is
     fetched as soon as the widget draws (the hidden drawer draws on every page view), so the src cannot be changed
     afterwards without a second download. Before the products draw (Rebuy's rebuy.beforeProductsChange), each
     Shopify CDN image URL in a Smart Cart widget gets width=300 and a fragment ending in ".rasz": Rebuy's suffix then
     lands in the fragment (never sent), and the CDN serves 300 px. Anything else (other hosts, a URL that already
     has width= or a fragment) is left as it is, so Rebuy's 400 px stays the fallback. */
  const REC_IMG_W = 300;
  const smallRecImage = (/** @type {unknown} */ src) =>
    typeof src === 'string' && /^(?:https?:)?\/\/cdn\.shopify\.com\/s\/files\//.test(src) && !/[?&]width=|#/.test(src)
      ? `${src}${src.includes('?') ? '&' : '?'}width=${REC_IMG_W}#.rasz`
      : src;
  document.addEventListener('rebuy.beforeProductsChange', (event) => {
    const detail = /** @type {any} */ (event).detail;
    const widget = detail?.widget;
    const inCart = widget?.data?.settings?.type === 'cart' || Boolean(widget?.element?.closest?.('#rebuy-cart'));
    if (!inCart || !Array.isArray(detail.products)) return;
    for (const product of detail.products) {
      if (product?.image?.src) product.image.src = smallRecImage(product.image.src);
      if (Array.isArray(product?.images)) for (const image of product.images) if (image?.src) image.src = smallRecImage(image.src);
    }
  });

  /* ---- room for the reel "Buy 3, get 1 free" row (Rebuy's ready callback adds it late) ---- */
  // The callback's own targets and rule (planFor, updated in Rebuy on 2026-10-16 for the sleeve offer).
  const B3G1 = ['reel-armour-spinning-reel-cover-1000-4000', 'reel-armour-baitcast-reel-cover', 'fishing-rod-sleeve-pro'];
  const B3G1_FREE = /buy\s*3\s*,?\s*get\s*1\s*free/i;
  const SLEEVE = 'fishing-rod-sleeve-pro';
  const lineHandle = (/** @type {Element} */ item) => B3G1.find((h) => item.classList.contains(`product-${h}`)) || '';
  const lineQty = (/** @type {Element} */ item) => {
    const n = (item.querySelector('.rebuy-cart__flyout-item-quantity-widget-label')?.textContent || '').match(/\d+/g);
    return n ? Number(n[n.length - 1]) || 0 : 0;
  };
  const freeLine = (/** @type {Element} */ item) => {
    let text = '';
    item.querySelectorAll('.rebuy-cart__flyout-item-discount-line, .rebuy-cart__flyout-item-discount-message').forEach((d) => (text += ` ${d.textContent}`));
    return B3G1_FREE.test(text) || /\$\s*0\.00/.test(item.querySelector('.rebuy-money.sale')?.textContent || '');
  };
  /** The line's own price reads $0.00 (the drawn price, when the cart is a beat behind the drawer). */
  const freePrice = (/** @type {Element} */ item) => /^\s*\$\s*0\.00\s*$/.test(item.querySelector('.rebuy-cart__flyout-item-price .rebuy-money.sale > span:not(.sr-only)')?.textContent || '');
  /** @type {WeakMap<Element, number>} */
  const waitSince = new WeakMap();
  let rowTimer = 0;

  /** Asks the cart's callback (NEW DEALS cart: window.__raTierPlan / __raStarsPlan) about a line; null if it has no such hook. */
  function askCart(/** @type {string} */ hook, /** @type {Element} */ item) {
    const fn = /** @type {any} */ (window)[hook];
    if (typeof fn !== 'function') return null;
    try {
      return Boolean(fn(item));
    } catch {
      return false;
    }
  }

  function syncRows(/** @type {HTMLElement} */ drawer) {
    // Only while Rebuy's callback is the one adding rows (it keeps its cache on window, or says per line).
    const planned = typeof (/** @type {any} */ (window).__raTierPlan) === 'function';
    if (!planned && !(/** @type {any} */ (window).__raB3g1Products)) return;
    const items = Array.from(drawer.querySelectorAll('.rebuy-cart__flyout-item'));
    let recheck = false;
    /** @type {Record<string, number>} */ const total = {};
    /** @type {Record<string, boolean>} */ const free = {};
    for (const item of items) {
      const h = lineHandle(item);
      if (!h) continue;
      total[h] = (total[h] || 0) + lineQty(item);
      if (freeLine(item)) free[h] = true;
    }
    for (const item of items) {
      const h = lineHandle(item);
      // Rebuy's rule per product: reels get the row under 4 units with no free line yet; the Rod Sleeve
      // (the % tiers: "Buy 4+ save 15%" under 4, "6+ save 20%" at 4-5, "8+ save 25%" at 6-7) under 8 units.
      // The NEW DEALS cart answers per line (one row, on the first line of a product); 1/09 LIVE does not.
      const asked = planned ? askCart('__raTierPlan', item) : null;
      const wants = asked !== null ? asked : Boolean(h) && (h === SLEEVE ? total[h] < 8 : !free[h] && total[h] < 4);
      const due = wants && lineQty(item) > 0 && !item.querySelector('.ra-b3g1-row');
      if (!due) waitSince.delete(item);
      else if (!waitSince.has(item)) waitSince.set(item, Date.now());
      // If the row never comes (lookup failed), the space is given back after 2 s.
      const keep = due && Date.now() - (waitSince.get(item) || 0) < 2000;
      // A data attribute, not a class: Vue rewrites the line's class attribute when it redraws.
      if (item.hasAttribute('data-ra-b3g1-wait') !== keep) item.toggleAttribute('data-ra-b3g1-wait', keep);
      if (keep) recheck = true;
    }
    // One timer re-checks a reservation that has run out (no row came), so it is given back.
    if (recheck && !rowTimer) rowTimer = window.setTimeout(() => ((rowTimer = 0), syncRows(drawer)), 2100);
  }

  /* The callback's star line under each line title ("★★★★★ (109 reviews)"), also added about 200 ms
     late: the lines of the products it rates reserve its 22px row plus the 7px gap under the title. */
  const STARRED = /(^|\s)product-(lureshield-fishing-lure-cover(-(5|10|15)-pack)?|reel-armour-(spinning-reel-cover-1000-4000|value-bundle|baitcast-reel-cover|baitcaster-edition-value-bundle)|fishing-rod-sleeve-pro|complete-solution-4-pack-pro-1|wrapstrap-rod-strap)(\s|$)/;
  /** @type {WeakMap<Element, number>} */
  const starSince = new WeakMap();
  let starTimer = 0;
  function syncStars(/** @type {HTMLElement} */ drawer) {
    const planned = typeof (/** @type {any} */ (window).__raStarsPlan) === 'function';
    if (!planned && !(/** @type {any} */ (window).__raStars2Obs)) return;
    let recheck = false;
    drawer.querySelectorAll('.rebuy-cart__flyout-item').forEach((item) => {
      // The NEW DEALS cart knows which lines get stars (USA Yotpo counts, none at 0 reviews); else the old list.
      const rated = planned ? askCart('__raStarsPlan', item) : STARRED.test(item.className);
      const due = Boolean(rated) && !item.querySelector('.ra-stars');
      if (!due) starSince.delete(item);
      else if (!starSince.has(item)) starSince.set(item, Date.now());
      const keep = due && Date.now() - (starSince.get(item) || 0) < 2000;
      if (item.hasAttribute('data-ra-stars-wait') !== keep) item.toggleAttribute('data-ra-stars-wait', keep);
      if (keep) recheck = true;
    });
    if (recheck && !starTimer) starTimer = window.setTimeout(() => ((starTimer = 0), syncStars(drawer)), 2100);
  }

  /* ---- savings: the deal only, never the compare-at markdown (Curtis 2026-10-16) ----
     Rebuy strikes each line's compare-at price and the compare-at subtotal, and the callback's
     "You're saving" strip is that subtotal minus the final one, so 5 sleeves read "$43.99" saved
     where the page says $14.99. From the cart Rebuy holds (Shopify's cart JSON):
     - a line with a deal (original_line_price > final_line_price) is marked data-ra-deal and its
       struck price is the line's own unit price before the deal; other lines show no struck price (CSS);
     - the struck subtotal is original_total_price when the cart has a discount, else hidden;
     - the strip, which the callback works out from those two subtotal figures, then reads
       total_discount; its amount is also set here directly.
     Text is only written when it differs, so neither Vue's redraws nor the callback loop on it. */
  const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  const setText = (/** @type {Element | null} */ el, /** @type {string} */ value) => {
    if (el && el.textContent !== value) el.textContent = value;
  };
  /* Bundles (Curtis 2026-10-19): their product pages strike the separate-items price ($139.96 -> $104.97), so their
     cart lines keep Rebuy's struck compare-at price too (Complete Solution: compare-at $99.90 set in Admin 2026-10-19), and that saving counts in the struck subtotal and the
     "You're saving" line. Only these handles (LureShield Pro XL has a compare-at its page does not show). */
  const BUNDLES = new Set([
    'reel-armour-value-bundle',
    'reel-armour-baitcaster-edition-value-bundle',
    'lureshield-fishing-lure-cover-5-pack',
    'lureshield-fishing-lure-cover-10-pack',
    'lureshield-fishing-lure-cover-15-pack',
    'complete-solution-4-pack-pro-1',
  ]);
  const cents = (/** @type {string | null | undefined} */ t) => {
    const n = Number(String(t || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
  };
  function syncSavings(/** @type {HTMLElement} */ drawer) {
    const cart = /** @type {any} */ (window).Rebuy?.Cart?.cart;
    if (!cart || !Array.isArray(cart.items)) return;
    const lines = Array.from(drawer.querySelectorAll('.rebuy-cart__flyout-item'));
    const sameOrder =
      lines.length === cart.items.length && lines.every((line, i) => !cart.items[i].handle || line.classList.contains(`product-${cart.items[i].handle}`));
    let bundleSaved = 0;
    lines.forEach((line, i) => {
      const item = sameOrder ? cart.items[i] : null;
      const deal = Boolean(item) && Number(item.original_line_price) > Number(item.final_line_price);
      if (line.hasAttribute('data-ra-deal') !== deal) line.toggleAttribute('data-ra-deal', deal);
      /* Audit 2026-10-21: the $0.00 line of a deal (Reel "Buy 3, get 1 free": Shopify splits 4 covers into 3 paid + 1
         free, two lines with their own keys) is managed by the discount, so its − / + are hidden (CSS): a + there wrote
         a second free-line unit, which Shopify then charged ($34.99) while the shopper thought it was free. */
      const free = item ? Number(item.final_line_price) === 0 && Number(item.original_line_price) > 0 : freePrice(line);
      if (line.hasAttribute('data-ra-free') !== free) line.toggleAttribute('data-ra-free', free);
      const was = line.querySelector('.rebuy-money.compare-at > span:not(.sr-only)');
      /* a bundle keeps Rebuy's compare-at as its struck price even with a deal (a code) on it; the deal part is
         already in total_discount, so only compare-at minus the price before the deal is added (review 2026-10-19) */
      const wasUnit = item && BUNDLES.has(item.handle) ? cents(was && was.textContent) : 0;
      const bundle = wasUnit > Number(item && item.original_price);
      if (line.hasAttribute('data-ra-bundle') !== bundle) line.toggleAttribute('data-ra-bundle', bundle);
      if (bundle) bundleSaved += (wasUnit - Number(item.original_price)) * Number(item.quantity || 1);
      else if (deal) setText(was, ` ${fmt.format(Number(item.original_price) / 100)}`);
    });
    const saved = (Number(cart.total_discount) || 0) + bundleSaved;
    const compare = drawer.querySelector('.rebuy-cart__flyout-subtotal-compare-amount');
    if (compare) {
      if (compare.hasAttribute('data-ra-hide') !== !saved) compare.toggleAttribute('data-ra-hide', !saved);
      setText(compare.querySelector('span:not(.sr-only)'), saved ? ` ${fmt.format((Number(cart.original_total_price) + bundleSaved) / 100)}` : '');
    }
    const strip = drawer.querySelector('[data-ra-cro]');
    if (strip) {
      if (strip.hasAttribute('hidden') !== !saved) strip.toggleAttribute('hidden', !saved);
      if (saved) setText(strip.querySelector('.ra-cro-strip-amt'), fmt.format(saved / 100));
    }
    // The theme's own "You're saving" line above the subtotal (Rebuy's strip was removed on 2026-10-16
    // because it showed the compare-at difference). Placed where that strip sat; put back if a redraw
    // moves it, removed when the cart has no deal.
    const sub = drawer.querySelector('.rebuy-cart__flyout-subtotal');
    // Prices are before sales tax (Curtis 2026-10-21), so the drawer says so under the subtotal, as /cart does.
    // Placed after the subtotal's whole row (a flex row that Rebuy lays out differently with and without a deal),
    // so it always gets its own line (audit 2026-10-21: it squeezed in beside "Subtotal" when there was no deal).
    const subRow = sub ? sub.parentElement : null;
    if (subRow && subRow.parentNode && !subRow.nextElementSibling?.hasAttribute('data-ra-tax')) {
      drawer.querySelectorAll('[data-ra-tax]').forEach((n) => n.remove());
      const note = document.createElement('p');
      note.className = 'ra-taxnote';
      note.setAttribute('data-ra-tax', '');
      note.textContent = 'Taxes calculated at checkout';
      subRow.after(note);
    }
    const row = sub && !strip ? sub.parentElement : null;
    if (!row || !row.parentNode || !saved) {
      savingLine?.remove();
      return;
    }
    if (!savingLine) {
      savingLine = document.createElement('p');
      savingLine.className = 'ra-saving';
      savingLine.setAttribute('data-ra-saving', '');
      savingLine.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M20.6 13.4 12 4.8A2 2 0 0 0 10.6 4H6a2 2 0 0 0-2 2v4.6c0 .5.2 1 .6 1.4l8.6 8.6a2 2 0 0 0 2.8 0l4.6-4.6a2 2 0 0 0 0-2.8z"/><circle cx="7.5" cy="7.5" r="1"/></svg>' +
        '<span>You’re saving</span> <strong></strong>';
    }
    setText(savingLine.querySelector('strong'), fmt.format(saved / 100));
    if (savingLine.nextElementSibling !== row || savingLine.parentNode !== row.parentNode) row.parentNode.insertBefore(savingLine, row);
  }
  /** @type {HTMLElement | null} */
  let savingLine = null;

  /* ---- the Reel "it's FREE" nudge (audit 2026-10-21) ----
     The cart's callback writes the next deal into the slot above the subtotal (.ra-save-slot: "Add 2 more to save
     15%", sleeves and LureShield only) and leaves it blank for the reels. With 3 covers of one kind and no free one yet,
     the slot gets data-ra-next="Add 1 more — it's FREE", which the CSS shows in the same place and style only while
     the callback's own text is empty (the sleeve / LureShield nudge keeps priority) and no saving shows. An attribute,
     not text: the callback's observer watches text and children, so the two never write over each other. Counted from
     the cart (per product, Spinning and Baitcast never mixed), never from a guess: exactly 3 of a kind, nothing free. */
  const REELS = ['reel-armour-spinning-reel-cover-1000-4000', 'reel-armour-baitcast-reel-cover'];
  const REEL_NEXT = 'Add 1 more \u2014 it\u2019s FREE';
  /** The slot's nudge for this cart: REEL_NEXT when a reel kind sits at 3 with no free line, else ''. Exported for tests. */
  function reelNext(/** @type {any} */ cart) {
    if (!cart || !Array.isArray(cart.items)) return '';
    return REELS.some((h) => {
      let qty = 0;
      let free = false;
      for (const it of cart.items) {
        if (!it || it.handle !== h) continue;
        qty += Number(it.quantity) || 0;
        if (Number(it.original_line_price) > Number(it.final_line_price)) free = true;
      }
      return qty === 3 && !free;
    })
      ? REEL_NEXT
      : '';
  }
  function syncNudge(/** @type {HTMLElement} */ drawer) {
    const text = reelNext(/** @type {any} */ (window).Rebuy?.Cart?.cart);
    drawer.querySelectorAll('.ra-save-slot').forEach((slot) => {
      if ((slot.getAttribute('data-ra-next') || '') === text) return;
      if (text) slot.setAttribute('data-ra-next', text);
      else slot.removeAttribute('data-ra-next');
    });
  }

  /* ---- "Complete your setup" length pickers (audit 2026-10-21) ----
     Rebuy's "Pick size" opened a dropdown already on "5ft - 6ft", so a tap on Add put that length in the cart unseen.
     Each picker in the drawer's widget gets a first, unpickable "Choose length" option (selected), and its Add stays
     disabled (aria-disabled, the tap is held back and the dropdown focused) until the shopper picks one. The lengths read
     "5–6 ft" (the option text node edited in place, so Vue keeps patching the same node; the values, the variant ids,
     are not touched). A picker Vue redraws is set up again on the next sync. */
  const fmtLen = (/** @type {string} */ t) => t.replace(/(\d+(?:\.\d+)?)\s*ft\s*[-\u2013]\s*(\d+(?:\.\d+)?)\s*ft\b/gi, '$1\u2013$2 ft');
  /** @type {WeakSet<HTMLSelectElement>} */
  const picked = new WeakSet();
  function syncPicks(/** @type {HTMLElement} */ drawer) {
    drawer.querySelectorAll('.rebuy-widget .rebuy-product-block').forEach((block) => {
      const select = /** @type {HTMLSelectElement | null} */ (block.querySelector('.rebuy-product-options select'));
      const button = block.querySelector('.rebuy-product-actions .rebuy-button');
      const waiting = Boolean(select && select.options.length > 1 && !picked.has(select));
      if (select) {
        let lengths = false;
        for (const label of block.querySelectorAll('.rebuy-product-options option:not([data-ra-ph]), .rebuy-size-label')) {
          for (const node of label.childNodes) {
            if (node.nodeType !== 3 || !node.nodeValue) continue;
            const next = fmtLen(node.nodeValue);
            if (next !== node.nodeValue) node.nodeValue = next;
            if (/\d\s*ft\b/i.test(next)) lengths = true;
          }
        }
        let ph = /** @type {HTMLOptionElement | null} */ (select.querySelector('option[data-ra-ph]'));
        if (waiting && !ph) {
          ph = document.createElement('option');
          ph.value = '';
          ph.disabled = true;
          ph.hidden = true;
          ph.setAttribute('data-ra-ph', '');
          ph.textContent = lengths ? 'Choose length' : 'Choose an option';
          select.insertBefore(ph, select.firstChild);
        }
        if (waiting && ph && select.selectedIndex !== 0) select.selectedIndex = 0;
      }
      if (block.hasAttribute('data-ra-pick-wait') !== waiting) block.toggleAttribute('data-ra-pick-wait', waiting);
      if (button && (button.getAttribute('aria-disabled') === 'true') !== waiting) {
        if (waiting) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      }
    });
  }
  // The shopper's own pick (a change event: Vue's v-model reads the same one) opens Add.
  document.addEventListener(
    'change',
    (event) => {
      const select = /** @type {any} */ (event.target);
      if (!(select instanceof HTMLSelectElement) || !select.closest('#rebuy-cart .rebuy-widget .rebuy-product-options') || !select.value) return;
      picked.add(select);
      const drawer = document.getElementById('rebuy-cart');
      if (drawer) syncPicks(drawer);
    },
    true
  );
  // Add before a length is picked: held back, and the dropdown is focused instead.
  for (const type of ['click', 'pointerdown']) {
    document.addEventListener(
      type,
      (event) => {
        const target = /** @type {any} */ (event.target);
        const button = target instanceof Element ? target.closest('#rebuy-cart .rebuy-product-block[data-ra-pick-wait] .rebuy-product-actions .rebuy-button') : null;
        if (!button) return;
        if (event.cancelable) event.preventDefault();
        event.stopImmediatePropagation();
        if (type === 'click') /** @type {HTMLElement | null} */ (button.closest('.rebuy-product-block')?.querySelector('.rebuy-product-options select'))?.focus();
      },
      { capture: true, passive: false }
    );
  }

  function syncDrawer(/** @type {HTMLElement} */ drawer) {
    fixTrust(drawer);
    syncSavings(drawer);
    syncNudge(drawer);
    syncPicks(drawer);
    syncRows(drawer);
    syncStars(drawer);
    syncWallets(drawer);
  }

  // Rebuy redraws the drawer on every cart change, so the line is fixed again after each redraw.
  function watchTrust() {
    const drawer = document.getElementById('rebuy-cart');
    if (!drawer) {
      // Not drawn yet: wait for Rebuy to add it to the page.
      const wait = new MutationObserver(() => {
        if (!document.getElementById('rebuy-cart')) return;
        wait.disconnect();
        watchTrust();
      });
      wait.observe(document.body, { childList: true, subtree: true });
      return;
    }
    syncDrawer(drawer);
    new MutationObserver(() => syncDrawer(drawer)).observe(drawer, { childList: true, subtree: true, characterData: true });
    // is-visible / has-items are class changes on the drawer itself: watched on their own, not subtree.
    new MutationObserver(() => syncWallets(drawer)).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    // Rebuy hides the closed drawer from screen readers (aria-hidden) but leaves its ~19 links and
    // buttons in the Tab order, off screen. `inert` follows aria-hidden so Tab skips the closed drawer.
    const syncInert = () => {
      drawer.inert = drawer.getAttribute('aria-hidden') === 'true';
    };
    syncInert();
    new MutationObserver(syncInert).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  /*
   * G10 (2026-10-19): no cart writes on a page view.
   * A first-time visitor has no `cart` cookie. Rebuy then holds its attribute-only cart writes (`_attribution` /
   * `_source` "Smart Cart 2.0", the progress bar's `_barId`, `_rebuyCartId`) until its first pointerdown / touchstart /
   * keydown / wheel, and sends them without a cookie, which makes a new cart for every visitor who touches the page.
   * Rebuy reads them only for an order (revenue attribution, its progress bar / Buy More Save More), so here they wait
   * until the visitor has a cart: the first add makes it (ensureCart() in the add paths, so every write lands in that
   * one cart), then they go out once the drawer is open (releaseHeld), so they do not hold up its first draw. A
   * visitor who never adds gets no cart and no write. Rebuy's own
   * state and methods; if a Rebuy update renames them this does nothing and Rebuy holds until a tap, as before.
   * The theme no longer makes a cart when Rebuy loads (it sent POST /cart/update.js {} on every first view, and that
   * cookie let Rebuy's 3 writes out at once: 4 writes per product view).
   */
  const CART_COOKIE = /(?:^|;\s*)cart=[^;]/;
  const hasCart = () => CART_COOKIE.test(document.cookie);
  function gateHeldWrites(/** @type {any} */ rc) {
    if (!rc || rc.__raGate || typeof rc.shouldHoldCartWrite !== 'function' || typeof rc.releaseHeldCartWrites !== 'function' ||
      typeof rc.isAttributionOnlyCartWrite !== 'function') return;
    const hold = rc.shouldHoldCartWrite;
    const release = rc.releaseHeldCartWrites;
    rc.shouldHoldCartWrite = function (/** @type {any} */ request) {
      // @ts-ignore
      if (hold.apply(this, arguments)) return true;
      try {
        return !hasCart() && Boolean(rc.isAttributionOnlyCartWrite(request));
      } catch {
        return false;
      }
    };
    rc.releaseHeldCartWrites = function () {
      if (!hasCart()) return undefined;
      // @ts-ignore
      return release.apply(this, arguments);
    };
    rc.__raGate = true;
  }
  /** The visitor has a cart now: send what Rebuy held. Called once the drawer is open after an add (so the writes do
   *  not hold up its first draw), after a silent add, and shortly after any Rebuy cart change (releaseSoon). */
  function releaseHeld() {
    const rc = /** @type {any} */ (window).Rebuy?.Cart;
    if (!rc || !rc.__raGate || !hasCart() || !rc.heldRequests?.length) return;
    try {
      rc.releaseHeldCartWrites();
    } catch {}
  }
  let releaseTimer = 0;
  function releaseSoon() {
    if (releaseTimer) return;
    releaseTimer = window.setTimeout(() => {
      releaseTimer = 0;
      releaseHeld();
    }, 2500);
  }
  /** Theme wrappers on Rebuy.Cart, as soon as it exists (before its first write). Idempotent. */
  function wrapRebuyCart() {
    const rc = /** @type {any} */ (window).Rebuy?.Cart;
    if (!rc) return;
    gateHeldWrites(rc);
    dedupeCartIdWrites();
  }

  function load() {
    if (loading) return loading;
    loading = new Promise((resolve, reject) => {
      // Review m1: a retry after a failed load adds no second stylesheet.
      if (config.styleUrl && ![...document.querySelectorAll('link[rel="stylesheet"]')].some((l) => l.getAttribute('href') === config.styleUrl)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = config.styleUrl;
        document.head.append(link);
      }

      let script = document.querySelector('script[src*="cdn.rebuyengine.com/onsite/js/rebuy.js"]');
      if (!script) {
        script = document.createElement('script');
        script.src = config.scriptUrl;
        script.async = true;
        document.head.append(script);
      }
      const started = Date.now();
      // Rev 5.3: waits up to 30 s (it gave up after 4 s, and that refusal stayed for the visit, so an add made as
      // the first tap on a slow phone never opened the drawer). A cart link still falls back to the cart page
      // after 4 s (openOrCartPage); a failed load can be tried again.
      const fail = (/** @type {Error} */ error) => {
        window.clearInterval(timer);
        loading = null;
        reject(error);
      };
      // Review m1: the failed tag is removed, so the next load() (a later tap) downloads Rebuy again instead of
      // finding the dead tag and polling for 30 s.
      script.addEventListener(
        'error',
        () => {
          script.remove();
          fail(new Error('Rebuy failed to load'));
        },
        { once: true }
      );
      const timer = window.setInterval(() => {
        wrapRebuyCart();
        const cart = smartCart();
        if (cart) {
          window.clearInterval(timer);
          resolve(cart);
        } else if (Date.now() - started > 30000) {
          fail(new Error('Rebuy Smart Cart was not ready in time'));
        }
      }, 100);
    });

    loading.then(watchTrust).catch(() => {});
    loading.then(wrapRebuyCart).catch(() => {});
    // Loaded ahead of a cart tap (scroll, earlier tap, hover): once the drawer has drawn and gone
    // quiet it can open with no shift, so later opens skip the skeleton.
    loading
      .then((cart) => {
        if (silentUntil > Date.now()) cart.skip_open = true;
        return whenDrawer();
      })
      // No drawer (Rebuy's first cart read failed: dropped signal, a 429) is not "drawn": later taps
      // keep the cold path, which can still fall back to /cart (go-live review 22 Oct).
      .then((drawer) =>
        drawer
          ? settled(drawer, 4000).then(() => {
              if (!cold) drawn = true;
            })
          : undefined
      )
      .catch(() => {});
    return loading;
  }

  /*
   * Rebuy holds its attribution cart write (cart/update.js with its `_source` / `_attribution`
   * attributes) until it sees a first pointerdown, touchstart, keydown or wheel. Rebuy is started BY
   * that first interaction (above), so it never sees it and waits for the second tap: then the write
   * goes out, the cart and recommendations are fetched again and the hidden Smart Cart carousel is
   * redrawn, about 200-250 ms of main thread at 4x CPU in the middle of the visitor's tapping (INP).
   * When a real interaction started Rebuy, mark it as seen once the page is idle and release what is
   * held, so that work runs at idle. Uses Rebuy.Cart's own state and method; if a Rebuy update
   * renames them this does nothing and Rebuy waits for a tap as before. Not on a scroll-only start
   * (Rebuy does not count a scroll) and not for the cart page's timed start.
   * G10: while the visitor has no cart, the release is a no-op (gateHeldWrites); the first add releases them.
   */
  function settleWhenIdle() {
    const idle = window.requestIdleCallback
      ? (/** @type {() => void} */ run) => window.requestIdleCallback(run, { timeout: 2000 })
      : (/** @type {() => void} */ run) => window.setTimeout(run, 200);
    idle(() => {
      const cart = /** @type {any} */ (window.Rebuy && window.Rebuy.Cart);
      if (!cart || cart._interactionSettled !== false || typeof cart.releaseHeldCartWrites !== 'function') return;
      cart._interactionSettled = true;
      // An add still in flight: Rebuy releases after it, on its own path.
      if (!cart.pendingNativeCartAdd) cart.releaseHeldCartWrites();
    });
  }

  /** Resolves with Rebuy's drawer element once it is in the page (null after `cap` ms). */
  function whenDrawer(cap = 6000) {
    const found = document.getElementById('rebuy-cart');
    if (found) return Promise.resolve(found);
    return new Promise((resolve) => {
      const wait = new MutationObserver(() => {
        const drawer = document.getElementById('rebuy-cart');
        if (!drawer) return;
        wait.disconnect();
        window.clearTimeout(timer);
        resolve(drawer);
      });
      const timer = window.setTimeout(() => (wait.disconnect(), resolve(null)), cap);
      wait.observe(document.body, { childList: true, subtree: true });
    });
  }

  /** Resolves once `el` has had no DOM change or image load for `gap` ms (at most `cap` ms). */
  function quiet(el, gap, cap) {
    return new Promise((resolve) => {
      let timer = 0;
      const done = () => {
        watch.disconnect();
        el.removeEventListener('load', bump, true);
        window.clearTimeout(timer);
        window.clearTimeout(limit);
        resolve(undefined);
      };
      const bump = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(done, gap);
      };
      const watch = new MutationObserver(bump);
      watch.observe(el, { childList: true, subtree: true, characterData: true });
      el.addEventListener('load', bump, true);
      const limit = window.setTimeout(done, cap);
      bump();
    });
  }

  /*
   * Resolves once the drawer is quiet and its "Complete your setup" carousel has arrived and
   * mounted (Rebuy fetches it after the drawer first shows, 0.4-0.5 s later; it pushes the trust
   * line down and resizes when Splide mounts), or after `cap` ms if it never comes.
   */
  const filled = (/** @type {Element} */ drawer) => {
    const grid = drawer.querySelector('.rebuy-widget .rebuy-product-grid');
    return Boolean(grid && grid.querySelector('.rebuy-product-block') && (!grid.classList.contains('splide') || grid.classList.contains('is-initialized')));
  };

  function settled(drawer, cap) {
    const start = Date.now();
    const step = () =>
      quiet(drawer, 350, Math.max(0, cap - (Date.now() - start))).then(() =>
        filled(drawer) || Date.now() - start >= cap ? undefined : step()
      );
    return step();
  }

  // Only the confirmed core cart controls the fast reveal. Recommendation fetches/images may
  // finish later; an unknown template or pending write takes the existing settling path.
  const coreSig = (cart) => cartSig(cart) && `${cartSig(cart)}|${cart.currency}|${cart.items.map((item) => `${item.variant_id}:${item.final_price}`).join(',')}`;
  const moneyCents = (node) => {
    const amounts = (node?.textContent || '').match(/\$\s*[\d,]+\.\d{2}/g);
    return amounts?.length === 1 ? Math.round(Number(amounts[0].replace(/[^\d.]/g, '')) * 100) : null;
  };
  const coreVariant = (title) => String(title || '').trim().replace(/(\d+(?:\.\d+)?)\s*ft\s*[-\u2013]\s*(\d+(?:\.\d+)?)\s*ft\b/gi, '$1\u2013$2 ft');
  function coreReady(drawer, proof) {
    const rc = /** @type {any} */ (window).Rebuy?.Cart;
    if (!proof || !rc || typeof proof.seq !== 'number' || proof.epoch !== cartEpoch || proof.seq !== rc.mutationSeq) return false;
    if (rc.pendingNativeCartAdd || rc.queue?.length || drawer.querySelector('[data-ra-pending], [data-ra-money-pending]')) return false;
    const fresh = proof.cart;
    if (!fresh.items.length || coreSig(fresh) !== coreSig(rc.cart)) return false;
    const lines = [...drawer.querySelectorAll('.rebuy-cart__flyout-item')].filter((line) => !line.parentElement?.closest('.rebuy-cart__flyout-item'));
    if (lines.length !== fresh.items.length) return false;
    const matched = lines.every((line, i) => {
      const item = fresh.items[i];
      const input = line.querySelector('.rebuy-cart__flyout-item-quantity-widget-input');
      const quantity = input ? Number(input.value) : lineQty(line);
      const price = line.querySelector('.rebuy-cart__flyout-item-price .rebuy-money:not(.compare-at)');
      const variant = line.querySelector('.rebuy-cart__flyout-item-variant-title')?.textContent?.trim() || '';
      return line.classList.contains(`product-${item.handle}`) && quantity === item.quantity &&
        (!item.variant_title || item.variant_title === 'Default Title' || coreVariant(variant) === coreVariant(item.variant_title)) &&
        moneyCents(price) === item.final_price;
    });
    const subtotal = drawer.querySelector('.rebuy-cart__flyout-subtotal-final-amount') || drawer.querySelector('.rebuy-cart__flyout-subtotal-amount');
    return matched && moneyCents(subtotal) === fresh.total_price;
  }
  function settledCore(drawer, cap, confirmation) {
    return new Promise((resolve) => {
      let finished = false;
      let timer = 0;
      const done = () => {
        if (finished) return;
        finished = true;
        window.clearTimeout(timer);
        resolve(undefined);
      };
      // A timeout is the old presentation fallback, never evidence of a confirmed cart.
      settled(drawer, cap).then(done);
      let refreshed = false;
      const check = (proof) => {
        if (finished || !proof) return;
        if (coreReady(drawer, proof)) return done();
        const rc = /** @type {any} */ (window).Rebuy?.Cart;
        const invalidated = rc && (proof.epoch !== cartEpoch || proof.seq !== rc.mutationSeq);
        // Attribution writes may land after the first read. Once those writes drain, refresh
        // the evidence once rather than waiting for unrelated recommendation images.
        if (!refreshed && invalidated && !rc.pendingNativeCartAdd && !rc.queue?.length &&
          !drawer.querySelector('[data-ra-pending], [data-ra-money-pending]')) {
          refreshed = true;
          syncCart().then(check).catch(() => {});
          return;
        }
        timer = window.setTimeout(() => check(proof), 40);
      };
      confirmation.then(check).catch(() => {});
    });
  }

  // Rebuy's drawer has drawn and gone quiet at least once: from then on it opens with no shift.
  let drawn = false;
  /** @type {null | { done: Promise<any>, abort: () => void }} */
  let cold = null;
  /** The cart link's fallback to /cart while Rebuy loads; closing the skeleton cancels it. @type {null | (() => void)} */
  let cancelCartLink = null;

  const SKELETON_CSS = `
.ra-rb-skel{position:fixed;inset:0;z-index:2147483638}
.ra-rb-skel__bg{position:absolute;inset:0;background:oklch(0 0 none / 0.7)}
.ra-rb-skel__panel{position:absolute;inset:0;display:flex;flex-direction:column;gap:1.25rem;padding:0.625rem 1rem 1rem;background:oklch(1 0 none);color:oklch(0.27 0 none);overflow:clip}
@media (width > 480px){.ra-rb-skel__panel{inset:10px 10px 10px auto;inline-size:min(500px,100% - 16px);border-radius:15px}}
.ra-rb-skel__head{display:flex;align-items:center;justify-content:space-between;min-block-size:2.75rem}
.ra-rb-skel__title{margin:0;font:700 0.875rem/1 var(--font-heading--family,sans-serif);text-transform:uppercase}
.ra-rb-skel__close{display:grid;place-items:center;inline-size:2.75rem;block-size:2.75rem;margin-inline-end:-0.5rem;padding:0;border:0;background:none;color:inherit;cursor:pointer;touch-action:manipulation}
.ra-rb-skel__close:focus-visible{outline:2px solid currentColor;outline-offset:-2px}
.ra-rb-skel__close:active{transform:scale(0.95)}
.ra-rb-skel__bar,.ra-rb-skel__img,.ra-rb-skel__txt,.ra-rb-skel__txt::after,.ra-rb-skel__sub,.ra-rb-skel__cta{border-radius:0.375rem;background:oklch(0.94 0 none)}
.ra-rb-skel__bar{block-size:0.75rem;border-radius:999px}
.ra-rb-skel__line{display:grid;grid-template-columns:5rem 1fr;gap:1rem;align-items:start}
.ra-rb-skel__img{aspect-ratio:1}
.ra-rb-skel__txt{block-size:1rem;inline-size:80%;margin-block-start:0.5rem;position:relative}
.ra-rb-skel__txt::after{content:"";position:absolute;inset-inline-start:0;inset-block-start:1.75rem;block-size:2.25rem;inline-size:60%}
.ra-rb-skel__foot{margin-block-start:auto;display:grid;gap:0.75rem}
.ra-rb-skel__sub{block-size:1.25rem}
.ra-rb-skel__cta{block-size:3rem;background:oklch(0.94 0.03 45)}
@media (prefers-reduced-motion:no-preference){
.ra-rb-skel__bg{animation:ra-rb-fade 250ms ease-out}
.ra-rb-skel__panel{animation:ra-rb-in 250ms cubic-bezier(0.23,1,0.32,1)}
.ra-rb-skel__close{transition:transform 160ms ease-out}
.ra-rb-skel__bar,.ra-rb-skel__img,.ra-rb-skel__txt,.ra-rb-skel__sub,.ra-rb-skel__cta{animation:ra-rb-pulse 1.2s ease-in-out infinite alternate}
}
@keyframes ra-rb-fade{from{opacity:0}}
@keyframes ra-rb-in{from{transform:translateX(30%);opacity:0}}
@media (width <= 480px){@keyframes ra-rb-in{from{transform:translateY(30%);opacity:0}}}
@keyframes ra-rb-pulse{to{opacity:0.55}}
html.ra-rb-filling #rebuy-cart,html.ra-rb-filling #rebuy-cart *{visibility:hidden!important}`;

  function buildSkeleton() {
    if (!document.getElementById('ra-rb-skel-css')) {
      const style = document.createElement('style');
      style.id = 'ra-rb-skel-css';
      style.textContent = SKELETON_CSS;
      document.head.append(style);
    }
    const box = document.createElement('div');
    box.className = 'ra-rb-skel';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Your cart');
    box.setAttribute('aria-busy', 'true');
    const line = '<div class="ra-rb-skel__line"><span class="ra-rb-skel__img"></span><span class="ra-rb-skel__txt"></span></div>';
    box.innerHTML =
      '<div class="ra-rb-skel__bg" data-close></div><div class="ra-rb-skel__panel">' +
      '<div class="ra-rb-skel__head"><p class="ra-rb-skel__title">Your cart</p>' +
      '<button type="button" class="ra-rb-skel__close" data-close aria-label="Close cart">' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg></button></div>' +
      '<div class="ra-rb-skel__bar"></div>' + line + line +
      '<div class="ra-rb-skel__foot"><span class="ra-rb-skel__sub"></span><span class="ra-rb-skel__cta"></span></div>' +
      '<p class="visually-hidden" role="status">Loading your cart</p></div>';
    return box;
  }

  /*
   * Rebuy is loaded or still loading, but its drawer has not drawn yet. Show the skeleton now,
   * keep Rebuy's drawer invisible while it slides in and fills, and swap it in once it is quiet,
   * so nothing visible moves (the slide-in falls outside the 500 ms after the tap that CLS forgives).
   */
  function coldOpen() {
    if (cold) return cold.done;
    const root = document.documentElement;
    const skeleton = buildSkeleton();
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    let closed = false;
    let shown = false;

    const finish = () => {
      root.classList.remove('ra-rb-filling');
      skeleton.remove();
      document.removeEventListener('keydown', onKey, true);
      cold = null;
    };
    const abort = () => {
      if (closed) return;
      closed = true;
      // The shopper closed the cart: no trip to /cart later (the 12 s fallback, or a late load failure).
      if (cancelCartLink) cancelCartLink();
      const cart = smartCart();
      if (shown && cart && typeof cart.hide === 'function') cart.hide();
      const hadFocus = skeleton.contains(document.activeElement);
      finish();
      if (hadFocus) opener?.focus({ preventScroll: true });
    };
    const onKey = (/** @type {KeyboardEvent} */ event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      abort();
    };

    root.classList.add('ra-rb-filling');
    document.body.append(skeleton);
    // The close button sits where the cart icon is on phones: a quick second tap on the icon is
    // not a close.
    const shownAt = Date.now();
    skeleton.addEventListener('click', (event) => {
      if (Date.now() - shownAt < 400) return;
      if (event.target instanceof Element && event.target.closest('[data-close]')) abort();
    });
    document.addEventListener('keydown', onKey, true);
    skeleton.querySelector('button')?.focus({ preventScroll: true });

    const done = load()
      .then((cart) => {
        if (closed) return cart;
        const confirmation = syncCart();
        cart.show();
        shown = true;
        // Rebuy's slide-in is 250 ms; its first fills arrive in bursts up to about 1 s apart.
        return whenDrawer()
          .then((drawer) => {
            // Rebuy never built its drawer: fail, so the tap goes to /cart and the next tap tries again.
            if (!drawer) throw new Error('Rebuy cart drawer did not appear');
            return settledCore(drawer, 2500, confirmation);
          })
          .then(() => {
            if (closed) return cart;
            const hadFocus = skeleton.contains(document.activeElement);
            drawn = true;
            finish();
            if (hadFocus) document.querySelector('#rebuy-cart .rebuy-cart__flyout-close')?.focus({ preventScroll: true });
            return cart;
          });
      })
      .catch((error) => {
        if (!closed) finish();
        throw error;
      });
    cold = { done, abort };
    return done;
  }

  /*
   * Rebuy's cart = Shopify's cart (stale drawer, Codex 2026-10-17: 12 items / $135.00 in the drawer, 13 / $141.99
   * at checkout, until a reload). Rebuy is loaded by the first tap or key. When that tap IS an Add to cart,
   * /cart/add.js leaves before Rebuy watches cart requests, and the cart.json Rebuy preloads as it starts is read
   * while the add is in flight: it holds the cart without the add, Rebuy's staleness check passes it (it never saw
   * a write), and nothing reads the cart again. So on every open and after every add announced here, /cart.js is
   * read; if Rebuy's copy differs, Rebuy is told a write happened (any read it started before now is then dropped)
   * and re-reads the cart itself. One read at a time; nothing is written.
   */
  const cartSig = (/** @type {any} */ c) =>
    c && Array.isArray(c.items) ? `${c.item_count}|${c.total_price}|${c.items.map((i) => `${i.key}:${i.quantity}`).join(',')}` : '';
  let cartEpoch = 0;
  /** @type {Promise<any> | null} */
  let syncing = null;
  const SYNC_MS = 8000;
  function syncCart() {
    const rc = /** @type {any} */ (window).Rebuy?.Cart;
    if (!rc || typeof rc.fetchCart !== 'function') return Promise.resolve();
    if (!syncing) {
      const read = (retry) => {
        const epoch = cartEpoch;
        const seq = rc.mutationSeq;
        // Limit both the read and its body; all callers share this read and at most one replacement.
        const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
        let timer = 0;
        const over = new Promise((_, fail) => {
          timer = window.setTimeout(() => {
            if (ctrl) ctrl.abort();
            fail(new Error('cart read timed out'));
          }, SYNC_MS);
        });
        return Promise.race([
          over,
          fetch(`${config.cartUrl || '/cart'}.js`, { headers: { Accept: 'application/json' }, ...(ctrl ? { signal: ctrl.signal } : {}) }).then(
            (response) => (response.ok ? response.json() : null)
          ),
        ])
          .catch(() => null)
          .finally(() => window.clearTimeout(timer))
          .then((fresh) => {
            // An after-add check may have joined a read started before that add. Replace it once,
            // so rejecting stale evidence cannot also discard the only reconciliation request.
            if (epoch !== cartEpoch || seq !== rc.mutationSeq) return retry ? read(false) : null;
            if (!fresh || !Array.isArray(fresh.items)) return null;
            const confirmed = JSON.parse(JSON.stringify(fresh));
            if (cartSig(fresh) !== cartSig(rc.cart)) {
              if (typeof rc.mutationSeq === 'number') rc.mutationSeq += 1;
              // fetchCart() is false while Rebuy has writes queued; each of those re-reads the cart when it lands.
              if (rc.fetchCart() === false && !rc.queue?.length && typeof rc.setCart === 'function') rc.setCart(fresh);
            }
            // Rebuy may decorate/mutate setCart's object; keep our read evidence independent.
            return { cart: confirmed, epoch, seq: rc.mutationSeq };
          });
      };
      syncing = read(true).catch(() => null).finally(() => { syncing = null; });
    }
    return syncing;
  }
  /** After an add: share the current check; an invalidated read receives one fresh replacement. */
  function syncAfterAdd() {
    load()
      .then(() => syncCart())
      .catch(() => {});
  }

  /*
   * Rebuy's itemHasBMSM() writes attributes[_rebuyCartId] each time it is called while the cart's value differs from
   * the active Smart Cart's id, and its templates call it several times per redraw before the cart is read again:
   * about 4 identical /cart/update.js per sync on a cart that lacks it (full-test trace). Its progress bar writes
   * attributes[_barId] the same way: twice per first view (G10, live 2026-10-19), the second before the first landed.
   * One is sent; the same value again within 4 s, or a value the cart already holds, is not.
   */
  const DEDUPE_ATTRS = ['_rebuyCartId', '_barId'];
  function dedupeCartIdWrites() {
    const rc = /** @type {any} */ (window).Rebuy?.Cart;
    if (!rc || typeof rc.updateItem !== 'function' || rc.updateItem.__raDedupe) return;
    const orig = rc.updateItem;
    /** @type {Record<string, { value: string, at: number }>} */
    const last = {};
    const wrapped = function (/** @type {any} */ data) {
      const attrs = data && typeof data === 'object' && !Array.isArray(data) ? data.attributes : null;
      const key = attrs && typeof attrs === 'object' && Object.keys(data).length === 1 && Object.keys(attrs).length === 1 ? Object.keys(attrs)[0] : '';
      if (DEDUPE_ATTRS.includes(key) && attrs[key] != null) {
        const value = String(attrs[key]);
        if (String(rc.cart?.attributes?.[key] ?? '') === value) return undefined;
        const prev = last[key];
        if (prev && prev.value === value && Date.now() - prev.at < 4000) return undefined;
        last[key] = { value, at: Date.now() };
      }
      // @ts-ignore
      return orig.apply(this, arguments);
    };
    /** @type {any} */ (wrapped).__raDedupe = true;
    rc.updateItem = wrapped;
  }

  function open() {
    load()
      .then(() => syncCart())
      .catch(() => {});
    const done = drawn && !cold ? load().then((cart) => cart.show()) : coldOpen();
    done.then(releaseHeld, () => {});
    return done;
  }

  /** Resolves when Rebuy's drawer is open after its next cart change, or after `cap` ms. */
  function rebuyOpened(cap) {
    return new Promise((resolve) => {
      const start = Date.now();
      const poll = () => {
        const cart = /** @type {any} */ (smartCart());
        if ((cart && cart.visible) || Date.now() - start >= cap) resolve(undefined);
        else window.setTimeout(poll, 50);
      };
      poll();
    });
  }

  // Silent adds: Rebuy opens its drawer on every cart change unless `skip_open` is set.
  let silentUntil = 0;
  let silentTimer = 0;
  function holdOpen(on) {
    const cart = smartCart();
    window.clearTimeout(silentTimer);
    if (!on) {
      silentUntil = 0;
      if (cart) cart.skip_open = false;
      return;
    }
    silentUntil = Date.now() + 10000;
    if (cart) cart.skip_open = true;
    silentTimer = window.setTimeout(() => holdOpen(false), 10000);
  }
  // Rebuy handles the change a little after its `rebuy:cart.change` event (it awaits the cart first).
  document.addEventListener('rebuy:cart.change', (event) => {
    releaseSoon();
    syncCartCount(event);
    if (!silentUntil) return;
    window.clearTimeout(silentTimer);
    silentTimer = window.setTimeout(() => holdOpen(false), 2500);
  });

  // Audit 2026-10-21: the drawer's − / + change the cart without the theme's cart events, so the header bubble (and
  // the count Horizon keeps in sessionStorage and puts back on the next page for 10 s) stayed on the old number
  // ("1" with 3 in the cart). Every Rebuy change now sets the bubble from the cart's own count.
  // Fleet review 2026-10-22: Rebuy nests the cart one level down (detail.cart.cart), so the count came from /cart.js,
  // and two quick changes could let the older response land last (2 -> 1). Both payload shapes are read, and only the
  // newest change's answer is applied.
  let countTimer = 0;
  let countGen = 0;
  /** @param {Event} event */
  function syncCartCount(event) {
    window.clearTimeout(countTimer);
    const gen = ++countGen;
    countTimer = window.setTimeout(async () => {
      const detail = /** @type {any} */ (event).detail;
      let count = detail?.cart?.item_count;
      if (typeof count !== 'number') count = detail?.cart?.cart?.item_count;
      if (typeof count !== 'number') {
        try {
          const root = window.Shopify?.routes?.root || '/';
          count = (await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json()).item_count;
        } catch {
          return;
        }
      }
      if (gen !== countGen || typeof count !== 'number') return;
      for (const icon of document.querySelectorAll('cart-icon')) {
        /** @type {any} */ (icon).renderCartBubble?.(count, false);
      }
    }, 300);
  }

  const CART_LINK = 'a.action__cart, a[data-ra-cart-open]';

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey) return;
    const link = event.target instanceof Element ? event.target.closest(CART_LINK) : null;
    if (!link || onCartPage()) return;

    event.preventDefault();
    // Rev 5.3: the skeleton stays until Rebuy is ready (a cold first visit on a slow phone needs 3-5 s, and every
    // early tap went to /cart at 4 s: speed audit); the cart page only after 12 s, or at once if Rebuy failed.
    let cancelled = false;
    const slow = window.setTimeout(() => {
      if (!cancelled) window.location.assign(link.href);
    }, 12000);
    if (cancelCartLink) cancelCartLink();
    cancelCartLink = () => {
      cancelled = true;
      window.clearTimeout(slow);
    };
    open().then(
      () => window.clearTimeout(slow),
      () => {
        window.clearTimeout(slow);
        if (!cancelled) window.location.assign(link.href);
      }
    );
  });

  // Start loading as soon as a cart link is pointed at, focused or touched.
  const warm = (/** @type {Event} */ event) => {
    if (event.target instanceof Element && event.target.closest(CART_LINK)) load().catch(() => {});
  };
  ['pointerover', 'focusin', 'touchstart'].forEach((type) => document.addEventListener(type, warm, { passive: true }));

  document.addEventListener('shopify:cart:lines-update', (event) => {
    // Shopify's standard event carries its payload on the event itself.
    const detail = /** @type {any} */ (event);
    if (detail.action === 'add') cartEpoch += 1;
    if (onCartPage() || detail.action !== 'add' || detail.context === 'cart') return;
    const added = Promise.resolve(detail.promise).then((/** @type {any} */ result) => {
      if (!(result && result.detail && result.detail.didError)) syncAfterAdd();
    });
    added.catch(() => {});
    if (detail.raSilent) {
      holdOpen(true);
      // The add made the cart: Rebuy's held attribute writes go to it now (no drawer to wait for).
      Promise.resolve(detail.promise).finally(releaseHeld).catch(() => {});
      return;
    }
    holdOpen(false);
    // Rebuy may open itself when the add lands: keep its drawer invisible until open() takes over.
    const hide = !drawn;
    if (hide) document.documentElement.classList.add('ra-rb-filling');
    // Rebuy drawn already: let it open itself once it has the new cart (our show() came first and
    // the new line then pushed "Complete your setup" down inside the open drawer). open() follows
    // as a fallback, and does nothing if the drawer is already open.
    const opened = hide ? null : rebuyOpened(2500);
    Promise.resolve(detail.promise)
      .then((/** @type {any} */ result) => {
        // Horizon resolves (not rejects) a refused add, with didError, and shows its message.
        if (result && result.detail && result.detail.didError) throw new Error('add failed');
        return opened ? opened.then(open) : open();
      })
      .catch(() => {
        const cart = smartCart();
        if (hide && !cold && cart && cart.visible && typeof cart.hide === 'function') cart.hide();
      })
      .finally(() => {
        if (hide && !cold) document.documentElement.classList.remove('ra-rb-filling');
        // Once the drawer is open (or the add failed): Rebuy's held attribute writes, after its first draw.
        releaseHeld();
      });
  });

  const interactions = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'];
  const onInteraction = (/** @type {Event} */ event) => {
    interactions.forEach((type) => window.removeEventListener(type, onInteraction));
    const started = load();
    if (event.type !== 'scroll') started.then(settleWhenIdle).catch(() => {});
  };
  interactions.forEach((type) => window.addEventListener(type, onInteraction, { passive: true }));
  if (onCartPage()) window.addEventListener('load', () => window.setTimeout(load, 5000), { once: true });
  // Rev 5.3: everywhere else Rebuy also starts once the page has loaded and gone idle (a first tap still starts it
  // sooner), so it is ready, and watching the cart, before the first Add to cart. After the load event, so LCP and
  // the page's own requests come first.
  else {
    const idle = () => {
      if (loading) return;
      const go = () => load().catch(() => {});
      if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 4000 });
      else window.setTimeout(go, 1500);
    };
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }
})();
